import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Container,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  GitHub as GitHubIcon,
  CheckCircleOutlined as CheckCircleOutlinedIcon,
} from '@mui/icons-material';
import { authApi } from '@/services/api';
import Logo from '@/components/common/Logo';

const BULLET_POINTS = [
  'Autonomous agents that work Jira tickets end-to-end',
  'AI answers with expert routing in Google Chat',
  'Spec-driven development & code generation',
  'GitHub + Jira + Confluence sync, automated',
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle error query params from GitHub OAuth redirects
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'org_denied') {
      const org = searchParams.get('org') || 'the required organization';
      setError(`Access denied. You must be a member of ${org} on GitHub.`);
    } else if (errorParam === 'oauth_failed') {
      setError('GitHub authentication failed. Please try again.');
    } else if (errorParam === 'invalid_state') {
      setError('Authentication session expired. Please try again.');
    } else if (errorParam === 'account_disabled') {
      setError('Your account has been disabled. Contact an administrator.');
    }
  }, [searchParams]);

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.checkEmail(email);
      if (response.data.exists) {
        // User exists - redirect to login with password
        navigate('/login/password', { state: { email } });
      } else {
        // User doesn't exist - redirect to register
        navigate('/register', { state: { email } });
      }
    } catch (err) {
      setError('Failed to check email. Please try again.');
      console.error('Check email error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGitHubLogin = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.getGitHubAuthUrl();
      window.location.href = response.data.authorization_url;
    } catch {
      setError('GitHub login is not available. Please use email/password.');
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left panel — branding / marketing */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          width: '45%',
          flexShrink: 0,
          px: 6,
          py: 8,
          background: 'linear-gradient(135deg, #0d1b4b 0%, #1a237e 50%, #0a3d6b 100%)',
        }}
      >
        {/* IONOS Internal Tools label */}
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: '0.15em',
            mb: 3,
            fontSize: '0.7rem',
          }}
        >
          IONOS Internal Tools
        </Typography>

        {/* Logo + H2 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Logo size={48} />
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { md: '2.25rem', lg: '2.75rem' },
              lineHeight: 1.1,
            }}
          >
            AI Wingman
          </Typography>
        </Box>

        {/* Tagline */}
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.75)',
            mb: 4,
            lineHeight: 1.6,
            maxWidth: 400,
          }}
        >
          Your team's agentic support platform — codes, ships, and reviews 24/7. Engineers review PRs.
          That's it.
        </Typography>

        {/* Bullet points */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 5 }}>
          {BULLET_POINTS.map((point) => (
            <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <CheckCircleOutlinedIcon
                sx={{ color: 'primary.main', fontSize: '1.1rem', mt: '2px', flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.5 }}
              >
                {point}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* "See what it can do" link */}
        <Box sx={{ mb: 'auto' }}>
          <Typography
            component="span"
            variant="body2"
            onClick={() => navigate('/welcome')}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { opacity: 0.8 },
            }}
          >
            See what it can do →
          </Typography>
        </Box>

        {/* Bottom powered-by text */}
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 4, display: 'block' }}
        >
          Powered by Claude + Qwen3 · IONOS Cloud
        </Typography>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          px: 2,
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                {/* Show logo on mobile only (hidden on desktop where left panel shows it) */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2 }}>
                  <Logo size={56} />
                </Box>
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  AI Wingman
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Agentic Support Platform
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleCheckEmail}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={isLoading}
                  endIcon={
                    isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />
                  }
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                  }}
                >
                  {isLoading ? 'Checking...' : 'Continue'}
                </Button>
              </form>

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                disabled={isLoading}
                onClick={handleGitHubLogin}
                startIcon={<GitHubIcon />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                Continue with GitHub
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 3, display: 'block', textAlign: 'center' }}
              >
                Enter your email to sign in or create an account
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
}
